import { makeAutoObservable, runInAction } from 'mobx'

import { trpcClient } from '@maxus/website/lib'

const POLLING_INTERVAL_MS = 5_000
// 列表初始拉取条数；UI 无限滚动时通过 latestCount setter 递增（接口不分页，只放大单次条数）。
export const INITIAL_LATEST_COUNT = 20

type WorkflowListResult = Awaited<ReturnType<typeof trpcClient.project.workflow.list.query>>

export class ProgressStore {
  private _latestWorkflows: WorkflowListResult['latestWorkflows'] = []
  private _deliverableWorkflowMap: WorkflowListResult['deliverableWorkflowMap'] = {}
  private _workflows: WorkflowListResult['workflows'] = []
  private _workflowStageMap: WorkflowListResult['workflowStageMap'] = {}
  private _deliverableId: string | undefined
  private _requestedLatestCount = INITIAL_LATEST_COUNT
  private _hasMoreWorkflows = false
  private _currentDeliverableId: string | null = null
  private _settled = false
  _pollingTimer: ReturnType<typeof setTimeout> | undefined
  _requestGeneration = 0

  constructor() {
    makeAutoObservable(this, { _pollingTimer: false, _requestGeneration: false }, { autoBind: true })
    this.restartPolling()
  }

  // latestCount = 请求的最新条数（可被无限滚动递增）；接口据此 limit，不分页。
  get latestCount() {
    return this._requestedLatestCount
  }

  set latestCount(value: number) {
    if (value === this._requestedLatestCount) return
    this._requestedLatestCount = value
    this.restartPolling()
  }

  get currentDeliverableId() {
    return this._currentDeliverableId
  }

  // 当前入参（latestCount / deliverableId）是否已至少完成过一次轮询回填。
  // 每次改入参会在 restartPolling 里重置为 false，供消费方展示 loading。
  get settled() {
    return this._settled
  }

  get latestWorkflows() {
    return this._latestWorkflows
  }

  get deliverableWorkflowMap() {
    return this._deliverableWorkflowMap
  }

  get workflows() {
    return this._workflows
  }

  get workflowStageMap() {
    return this._workflowStageMap
  }

  // 上一轮是否取满（返回的最新条数 >= 请求条数）——即可能还有更多，供无限滚动判断。
  get hasMoreWorkflows() {
    return this._hasMoreWorkflows
  }

  get deliverableId() {
    return this._deliverableId
  }

  set deliverableId(value: string | undefined) {
    if (value === this._deliverableId) return
    this._deliverableId = value
    this.restartPolling()
  }

  dispose() {
    this._requestGeneration += 1
    if (this._pollingTimer) clearTimeout(this._pollingTimer)
    this._pollingTimer = undefined
  }

  private restartPolling() {
    if (this._pollingTimer) clearTimeout(this._pollingTimer)
    this._pollingTimer = undefined
    this._settled = false
    this._requestGeneration += 1
    void this.poll(this._requestGeneration)
  }

  private async poll(generation: number) {
    try {
      const result = await trpcClient.project.workflow.list.query({
        latestCount: this._requestedLatestCount,
        deliverableId: this._deliverableId,
      })
      if (generation !== this._requestGeneration) return

      runInAction(() => {
        this._latestWorkflows = result.latestWorkflows
        this._deliverableWorkflowMap = result.deliverableWorkflowMap
        this._workflows = result.workflows
        this._workflowStageMap = result.workflowStageMap
        this._hasMoreWorkflows = result.latestCount >= this._requestedLatestCount
        this._currentDeliverableId = result.deliverableId
        this._settled = true
      })
    } catch {
      return
    } finally {
      if (generation === this._requestGeneration) {
        this._pollingTimer = setTimeout(
          () => {
            void this.poll(generation)
          },
          POLLING_INTERVAL_MS,
        )
      }
    }
  }
}

export const progressStore = new ProgressStore()
